import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router";
import { useProgramData } from "@hooks/data/useProgramData";
import { IonBackButton, IonButtons, IonContent, IonHeader, IonPage, IonToolbar } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { HeaderTitle } from "parafolio-components";
import DataController from "@controllers/DataController";
import LearningOutcomesHeader from "@components/programs/LearningOutcomesHeader";
import * as ProgramUtils from "@utils/programUtils";
import LearningOutcomesList from "@components/programs/LearningOutcomesList";
import Container from "@components/common/Container";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { IEvidence, IUser, LearningObjective } from "@interfaces/Interfaces";
import { evidenceAtom, userAtom } from "@state/State";
import * as NQPUtils from "@utils/nqpUtils";
import { userRolesSelector } from "@state/UserState";
import { EventRegister } from "react-native-event-listeners";
import * as FirebaseService from "@controllers/FirebaseService";
import ProgramEvidenceList from "@components/programs/ProgramEvidenceList";
import { PROGRAM_IDS } from "@constants/programs";
import _ from "lodash";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import { subYears } from "date-fns";
import * as EvidenceUtils from "@utils/evidenceUtils";

const NQPLearningOutcomesPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string }>();
  const { userPrograms } = useProgramData();
  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const { currentProgramCycle } = useProgramCycles(params.programId);

  const program = userPrograms.find((item) => item.ID === params.programId)!;

  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);
  const programRoles = allUserRoles.find((role) => role.programID === params.programId);

  let userRoles: string[] = [];

  if (programRoles) {
    userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
  }

  let learningObjectives: LearningObjective[] = [];
  let groupedLearningOutcomes: Partial<Record<string, LearningObjective[]>>;

  if (userRoles.length > 0) {
    learningObjectives = ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]);
    groupedLearningOutcomes = ProgramUtils.getAllLearningObjectivesForRoleByGroup(program, userRoles[0]);
  } else {
    learningObjectives = ProgramUtils.getAllLearningObjectivesForProgram(program);
    groupedLearningOutcomes = ProgramUtils.getAllLearningObjectivesForProgramByGroup(program);
  }

  const mappedLearningOutcomes = Object.keys(groupedLearningOutcomes).map((key) => {
    const reduced = groupedLearningOutcomes[key]!.reduce(
      (acc, item) => {
        if (item.Section) {
          acc[item.Section!] = (acc[item.Section!] ?? []).concat(item).sort(ProgramUtils.sortLOsUsingDisplayName);
        } else {
          if (program.ID === PROGRAM_IDS.SWAST_SP_AP_RP) {
            acc[key] = (acc[key] ?? []).concat(item).sort(ProgramUtils.sortLOsUsingDisplayName);
          } else if (program.ID === PROGRAM_IDS.NWAS_ARC) {
            acc[key] = (acc[key] ?? []).concat(item).sort(ProgramUtils.sortLOsUsingDisplayName);
          } else {
            acc[key] = (acc[key] ?? []).concat(item);
          }
        }

        return acc;
      },
      {} as Record<string, LearningObjective[]>
    );

    return {
      [key]: reduced,
    };
  });

  let programEvidenceForList: IEvidence[] = [];
  let evidenceForProgram: IEvidence[] = [];
  let evidenceForCycle: IEvidence[] | undefined;

  if (allEvidence.state === "hasValue" && allEvidence.contents) {
    if (ProgramUtils.isNwasARC(program.ID)) {
      evidenceForProgram = allEvidence.contents.filter((evidence: IEvidence) => {
        if (!evidence.evidenceJSON) return false;

        const evidenceJSON = evidence.evidenceJSON;
        const id = evidenceJSON?.programInfo?.programID || "";

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: program.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: program["Evidence Definitions"] ?? [],
        });

        return (
          id === program?.ID &&
          (EvidenceUtils.evidenceHasLearningOutcomes(evidence) || programEvidenceType === "capabilities")
        );
      });

      programEvidenceForList = structuredClone(evidenceForProgram);

      evidenceForProgram = evidenceForProgram.filter((evidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: program.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: program["Evidence Definitions"] ?? [],
        });

        if (programEvidenceType === "capabilities") {
          return new Date(evidence.date) > subYears(new Date(), 2) && onHoldReset !== 1 && evidence.draft !== true;
        }

        return onHoldReset !== 1 && evidence.draft !== true;
      });
    } else if (ProgramUtils.isSPAPRP(program.ID)) {
      evidenceForProgram = allEvidence.contents.filter((evidence: IEvidence) => {
        if (!evidence.evidenceJSON) return false;

        const evidenceJSON = evidence.evidenceJSON;
        const id = evidenceJSON?.programInfo?.programID || "";

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: program.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: program["Evidence Definitions"] ?? [],
        });

        return (
          id === program?.ID &&
          (EvidenceUtils.evidenceHasLearningOutcomes(evidence) || programEvidenceType === "capabilities")
        );
      });

      programEvidenceForList = structuredClone(evidenceForProgram ?? []);

      if (currentProgramCycle) {
        evidenceForCycle = evidenceForProgram.filter((evidence) => {
          return evidence.evidenceJSON?.programInfo?.programCycle === currentProgramCycle;
        });
      }

      evidenceForCycle = evidenceForCycle?.filter((evidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        const programEvidenceType = ProgramUtils.getEvidenceType({
          evidenceJSON: evidence.evidenceJSON,
          usesAdditionalEvidenceDefinitions: program.UsesAdditionalEvidenceDefinitions ?? false,
          evidenceDefinitions: program["Evidence Definitions"] ?? [],
        });

        if (programEvidenceType === "capabilities") {
          return new Date(evidence.date) > subYears(new Date(), 2) && onHoldReset !== 1 && evidence.draft !== true;
        }

        return onHoldReset !== 1 && evidence.draft !== true;
      });
    } else {
      evidenceForProgram = allEvidence.contents.filter((evidence: IEvidence) => {
        const evidenceJSON = evidence.evidenceJSON;
        const id = evidenceJSON?.programInfo?.programID || "";

        const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

        return id === program?.ID && onHoldReset !== 1 && evidence.draft !== true;
      });

      programEvidenceForList = structuredClone(evidenceForProgram);
    }

    programEvidenceForList = _.orderBy(programEvidenceForList, "date", "desc");
    evidenceForProgram = _.orderBy(evidenceForProgram, "date", "desc");
  }

  const mandatoryLearningObjectives = useMemo(
    () => learningObjectives.filter((lo) => !lo.Optional),
    [learningObjectives]
  );
  const optionalLearningObjectives = useMemo(
    () => learningObjectives.filter((lo) => lo.Optional),
    [learningObjectives]
  );

  const useAutoLOs = program.AutoLOsForCalculations ?? false;
  const mandatoryCount = useMemo(
    () =>
      NQPUtils.getLearningOutcomesAchieved(
        mandatoryLearningObjectives,
        evidenceForCycle ?? evidenceForProgram,
        useAutoLOs
      ),
    [mandatoryLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );
  const optionalCount = useMemo(
    () =>
      NQPUtils.getLearningOutcomesAchieved(
        optionalLearningObjectives,
        evidenceForCycle ?? evidenceForProgram,
        useAutoLOs
      ),
    [optionalLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );
  const mandatoryTotal = mandatoryLearningObjectives.length;
  const optionalTotal = optionalLearningObjectives.length;
  const mandatoryPercentage = mandatoryTotal > 0 ? Math.trunc((mandatoryCount / mandatoryTotal) * 100) : 0;
  const optionalPercentage = optionalTotal > 0 ? Math.trunc((optionalCount / optionalTotal) * 100) : 0;

  const showPartialCount = useMemo(
    () => NQPUtils.learningOutcomesHavePartialState(mandatoryLearningObjectives),
    [mandatoryLearningObjectives]
  );
  const mandatoryPartialCount = useMemo(
    () =>
      showPartialCount
        ? NQPUtils.getLearningOutcomesPartiallyMet(
            mandatoryLearningObjectives,
            evidenceForCycle ?? evidenceForProgram,
            useAutoLOs
          )
        : undefined,
    [showPartialCount, mandatoryLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );
  const mandatoryPartialPercentage =
    mandatoryPartialCount !== undefined && mandatoryTotal > 0
      ? Math.trunc((mandatoryPartialCount / mandatoryTotal) * 100)
      : undefined;

  const showPartialCountOptional = useMemo(
    () => NQPUtils.learningOutcomesHavePartialState(optionalLearningObjectives),
    [optionalLearningObjectives]
  );
  const optionalPartialCount = useMemo(
    () =>
      showPartialCountOptional
        ? NQPUtils.getLearningOutcomesPartiallyMet(
            optionalLearningObjectives,
            evidenceForCycle ?? evidenceForProgram,
            useAutoLOs
          )
        : undefined,
    [showPartialCountOptional, optionalLearningObjectives, evidenceForCycle, evidenceForProgram, useAutoLOs]
  );
  const optionalPartialPercentage =
    optionalPartialCount !== undefined && optionalTotal > 0
      ? Math.trunc((optionalPartialCount / optionalTotal) * 100)
      : undefined;

  const addProgramEvidence = (params?: {
    type?: "skill" | "standard" | "capabilities";
    selectedLO?: LearningObjective;
  }): void => {
    let data: object = {};

    const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;
    const evidenceDefinitions = program["Evidence Definitions"] ?? [];
    const mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, program);
    const optionalStandards = ProgramUtils.getOptionalStandards(user.contents, program);

    if (params?.type === "capabilities") {
      const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "capabilities");
      const learningObjectives = ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]);

      data = {
        ...data,
        program: program,
        ...(evidenceDefinitions.length > 0 &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinition,
          }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(learningObjectives.length > 0 && { learningObjectives }),
        ...(params?.selectedLO && { preselectedLearningObjective: params.selectedLO }),
      };
    }

    FirebaseService.logEvent("program_add_enhanced_evidence", {
      program: program.Name,
    });

    EventRegister.emit("evidence/add-program-evidence", data);
  };

  const learningOutcomeRowPressed = (learningOutcome: LearningObjective): void => {
    addProgramEvidence({ type: "capabilities", selectedLO: learningOutcome });
  };

  const evidenceCardClicked = (item: IEvidence): void => {
    history.push(`/dashboard/program/${params.programId}/read/${item.id}`);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref={`/dashboard/program/${program?.ID}`}
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{program.LearningOutcomesName ?? "Learning Outcomes"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <Container>
          <LearningOutcomesHeader
            mandatoryCount={mandatoryCount}
            mandatoryTotal={mandatoryTotal}
            mandatoryPercentage={mandatoryPercentage}
            mandatoryPartialCount={mandatoryPartialCount}
            mandatoryPartialPercentage={mandatoryPartialPercentage}
            optionalCount={optionalCount}
            optionalPartialCount={optionalPartialCount}
            optionalPartialPercentage={optionalPartialPercentage}
            optionalTotal={optionalTotal}
            optionalPercentage={optionalPercentage}
            program={program}
            showEvidenceButton={ProgramUtils.showEvidenceButtonInLearningOutcomes(program.ID)}
            addProgramEvidence={addProgramEvidence}
            userRoleName={userRoles[0] ?? undefined}
          />
          <LearningOutcomesList
            learningOutcomes={mappedLearningOutcomes}
            evidence={evidenceForCycle ?? evidenceForProgram}
            useAutoLOs={useAutoLOs}
            hideTotal={(program.HideLOsTotal || ProgramUtils.isNwasARC(program.ID)) ?? false}
            useRAGForTotal={ProgramUtils.isNwasARC(program.ID)}
            rowsAsButtons={ProgramUtils.showLearningOutcomesListRowsAsButtons(program.ID)}
            learningOutcomeRowPressed={learningOutcomeRowPressed}
            sortLOsUsingDisplayName={ProgramUtils.isSPAPRP(program.ID)}
          />
          <ProgramEvidenceList
            evidence={programEvidenceForList}
            program={program}
            progressChecks={program.ProgressChecks ?? []}
            evidenceCardPressed={evidenceCardClicked}
          />
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default NQPLearningOutcomesPage;
